import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  House,
  Scales,
  ChatCircleDots,
  UserCircle,
  Sparkle,
} from "@phosphor-icons/react";
import { useUnread } from "../context/UnreadContext";
import { prefetchCases } from "../lib/casesStore";
import { prefetchConversations } from "../lib/conversationsStore";

const PREFETCH_BY_ROUTE = {
  "/cases": prefetchCases,
  "/messages": prefetchConversations,
};

/* Mobile floating pill bottom nav.
   Translucent glass effect + central animated AI button (gold pulse). */

const SIDE_TABS_LEFT = [
  { to: "/", label: "الرئيسية", icon: House, testid: "bnav-home", end: true },
  { to: "/cases", label: "القضايا", icon: Scales, testid: "bnav-cases" },
];

const SIDE_TABS_RIGHT = [
  { to: "/messages", label: "الرسائل", icon: ChatCircleDots, testid: "bnav-messages", badge: "unread" },
  { to: "/account", label: "حسابي", icon: UserCircle, testid: "bnav-account" },
];

const itemCls = (overlayActive) => ({ isActive }) => {
  const effectiveActive = isActive && !overlayActive;
  return `flex flex-col items-center justify-center gap-1 flex-1 py-2 transition-colors ${
    effectiveActive ? "text-[#0A192F]" : "text-[#8A8A8A] hover:text-[#0A192F]"
  }`;
};

const labelCls = (isActive) =>
  `text-[15px] font-tajawal leading-tight tracking-tight ${
    isActive ? "font-bold" : "font-medium"
  }`;

function SideTab({ tab, overlayActive }) {
  const { count: unreadCount } = useUnread();
  // Tapping any nav tile dispatches a global event so any open overlay
  // (notification sheet, future drawers) can dismiss itself in unison.
  const handleClick = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("lexiq:nav-tap", { detail: { to: tab.to } }));
    }
  };
  // P4 — prefetch destination data at pointer-down so the target list
  // is often already in memory by the time React mounts it.
  const handlePointerDown = PREFETCH_BY_ROUTE[tab.to];
  const badgeValue = tab.badge === "unread" ? unreadCount : 0;
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={itemCls(overlayActive)}
      data-testid={tab.testid}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    >
      {({ isActive }) => {
        const effectiveActive = isActive && !overlayActive;
        return (
          <>
            <span className="relative inline-flex">
              <tab.icon size={24} weight={effectiveActive ? "fill" : "regular"} />
              {badgeValue > 0 && (
                <span
                  className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E11D48] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_2px_6px_rgba(225,29,72,0.5)] border border-white"
                  data-testid={`${tab.testid}-badge`}
                  aria-label={`${badgeValue} رسالة غير مقروءة`}
                >
                  {badgeValue > 99 ? "99+" : badgeValue}
                </span>
              )}
            </span>
            <span className={labelCls(effectiveActive)}>{tab.label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

export default function BottomNav() {
  const location = useLocation();
  // When the Notification Sheet (or any future global overlay) is open we
  // are no longer "on" the home tab — deselect all tabs accordingly.
  const [overlayActive, setOverlayActive] = useState(false);

  useEffect(() => {
    const onOpen = () => setOverlayActive(true);
    const onClose = () => setOverlayActive(false);
    window.addEventListener("lexiq:notif-open", onOpen);
    window.addEventListener("lexiq:notif-close", onClose);
    return () => {
      window.removeEventListener("lexiq:notif-open", onOpen);
      window.removeEventListener("lexiq:notif-close", onClose);
    };
  }, []);

  // Floating bottom nav is visible across the core mobile flows
  // (الرئيسية، القضايا، الجدول، حسابي، القوانين). All other routes (chat,
  // settings, analyze, etc.) keep their own dedicated header / navigation
  // pattern.
  const BOTTOM_NAV_ROUTES = ["/", "/cases", "/timeline", "/account", "/laws", "/messages"];
  if (!BOTTOM_NAV_ROUTES.includes(location.pathname)) return null;

  return (
    <>
      {/* Spacer DISABLED on mobile in the new Layout — `<main>` now
       * provides its own bottom padding (= safe-area + 96px) so the
       * spacer was leaving a visible white strip behind the floating
       * nav at the very bottom of the viewport. Pages that render
       * outside <Layout> (AddStagePage, MessageRoom, mobile sub-pages)
       * compute the bottom padding themselves, so dropping the spacer
       * here is safe across the app. */}

      <nav
        className="lg:hidden fixed inset-x-3 z-[90]"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}
        dir="rtl"
        data-testid="bottom-nav"
      >
        <div
          className="relative mx-auto max-w-md rounded-[32px] shadow-[0_12px_40px_rgba(10,25,47,0.18)] border border-white/40 px-1.5 py-1"
          style={{
            // Apple-style UITabBar: 2% white tint + light blur. The
            // saturate(150%) boost makes the colors *behind* the bar pop
            // slightly through the glass — that's the actual iOS magic
            // that makes the bar feel see-through instead of cloudy.
            background: "rgba(255,255,255,0.005)",
            backdropFilter: "blur(8px) saturate(150%)",
            WebkitBackdropFilter: "blur(8px) saturate(150%)",
          }}
        >
          <div className="flex items-stretch">
            {SIDE_TABS_LEFT.map((t) => (
              <SideTab key={t.to} tab={t} overlayActive={overlayActive} />
            ))}

            {/* central AI button — lifted, animated gold pulse */}
            <div className="flex-1 flex items-end justify-center relative">
              <NavLink
                to="/chat"
                data-testid="bnav-ai-cta"
                onClick={() => window.dispatchEvent(new CustomEvent("lexiq:nav-tap", { detail: { to: "/chat" } }))}
                className="relative -mt-7 flex flex-col items-center justify-center w-[58px] h-[58px] rounded-full bg-gradient-to-br from-[#E6C463] to-[#B8860B] text-white shadow-[0_8px_22px_rgba(212,175,55,0.55)] border-[3px] border-white active:scale-95 transition-transform"
                aria-label="الأسئلة القانونية بالـ AI"
              >
                {/* pulsing halo */}
                <span className="absolute inset-0 rounded-full bg-[#D4AF37]/45 animate-lex-pulse pointer-events-none" />
                <span className="absolute inset-0 rounded-full bg-[#D4AF37]/30 animate-lex-pulse-2 pointer-events-none" />
                <Sparkle size={22} weight="fill" className="relative animate-lex-spin-slow" />
                <span className="relative text-[9px] font-bold leading-none mt-0.5 tracking-wider">AI</span>
              </NavLink>
            </div>

            {SIDE_TABS_RIGHT.map((t) => (
              <SideTab key={t.to} tab={t} overlayActive={overlayActive} />
            ))}
          </div>
        </div>
      </nav>

      <style>{`
        @keyframes lex-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.35); opacity: 0; }
        }
        @keyframes lex-pulse-2 {
          0%, 100% { transform: scale(1); opacity: 0.35; }
          50%      { transform: scale(1.6); opacity: 0; }
        }
        @keyframes lex-spin-slow {
          0%   { transform: rotate(0deg) scale(1); }
          50%  { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }
        .animate-lex-pulse   { animation: lex-pulse 2s ease-in-out infinite; }
        .animate-lex-pulse-2 { animation: lex-pulse-2 2.4s ease-in-out infinite; }
        .animate-lex-spin-slow { animation: lex-spin-slow 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .animate-lex-pulse, .animate-lex-pulse-2, .animate-lex-spin-slow { animation: none; }
        }
      `}</style>
    </>
  );
}
